import { useEffect, useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadSimple, File, Trash, DownloadSimple } from "@phosphor-icons/react";

const fmtSize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

export default function Files() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  const load = async () => {
    try {
      const { data } = await api.get("/files");
      setFiles(data.files);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    for (const f of Array.from(fileList)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Uploaded ${f.name}`);
      } catch (e) {
        toast.error(`${f.name}: ${formatApiError(e.response?.data?.detail)}`);
      }
    }
    setUploading(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/files/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-8">
      <header className="mb-6 pb-6 border-b border-[#262626]">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A] mb-2">Storage</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Files</h1>
        <p className="text-sm text-[#A1A1AA] mt-2">Max 25MB per file. Images, PDF, CSV, Office, ZIP.</p>
      </header>

      <div
        className={`border-2 border-dashed p-10 text-center mb-6 transition-colors ${
          drag ? "border-[#0055FF] bg-[#0055FF]/5" : "border-[#262626] bg-[#0F0F0F]"
        }`}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        data-testid="dropzone"
      >
        <UploadSimple size={28} weight="bold" className="text-[#71717A] mx-auto mb-3" />
        <p className="text-sm text-[#A1A1AA] mb-4">Drop files here or</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => upload(e.target.files)}
          data-testid="file-input"
        />
        <Button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]"
          data-testid="browse-files-btn"
        >
          {uploading ? "Uploading..." : "Browse files"}
        </Button>
      </div>

      <div className="space-y-2" data-testid="files-list">
        {files.length === 0 && (
          <div className="dashed border border-[#262626] p-12 text-center text-[#71717A]">
            No files uploaded yet.
          </div>
        )}
        {files.map((f) => (
          <div key={f.id} className="flex items-center gap-4 border border-[#262626] bg-[#141414] p-4">
            <File size={20} weight="bold" className="text-[#71717A]" />
            <div className="flex-1 min-w-0">
              <div className="text-sm truncate">{f.filename}</div>
              <div className="text-xs text-[#71717A] font-mono">
                {f.content_type} · {fmtSize(f.size)}
              </div>
            </div>
            <a
              href={`${API}${f.url.replace(/^\/api/, "")}`}
              target="_blank"
              rel="noreferrer"
              className="text-[#A1A1AA] hover:text-white p-2"
              data-testid={`download-file-${f.id}`}
            >
              <DownloadSimple size={16} weight="bold" />
            </a>
            <Button
              size="sm"
              variant="outline"
              className="rounded-sm border-[#262626] bg-transparent hover:bg-[#EF4444] hover:text-white"
              onClick={() => remove(f.id)}
              data-testid={`delete-file-${f.id}`}
            >
              <Trash size={12} />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
